import { useEffect, useRef, useState } from 'react';
import { ExternalLink, Pause, Play, X } from 'lucide-react';
import type { CandidateArchiveLink } from '@/lib/selections-api';

export interface PlayableSource {
  kind: 'soundcloud' | 'external';
  url: string;
  name?: string;
}

/** Pick a playable source from a show's archive links (SoundCloud preferred). */
export function playableSource(links: CandidateArchiveLink[] | undefined): PlayableSource | null {
  if (!links || links.length === 0) return null;
  for (const l of links) {
    if (l.url && (l.type === 'soundcloud' || /soundcloud\.com/i.test(l.url))) {
      return { kind: 'soundcloud', url: l.url };
    }
  }
  const any = links.find((l) => l.url);
  return any ? { kind: 'external', url: any.url, name: any.name } : null;
}

/** A row's play affordance: ▶ for SoundCloud (drives the shared bar), ↗ otherwise. */
export function RowPlay({ source, onPlay }: { source: PlayableSource | null; onPlay: () => void }) {
  if (!source) return null;
  if (source.kind === 'soundcloud') {
    return (
      <button
        type="button"
        onClick={onPlay}
        title="Play"
        className="text-muted-foreground hover:text-foreground"
      >
        <Play className="h-4 w-4" />
      </button>
    );
  }
  return (
    <a
      href={source.url}
      target="_blank"
      rel="noreferrer"
      title="Open"
      className="text-muted-foreground hover:text-foreground"
    >
      <ExternalLink className="h-4 w-4" />
    </a>
  );
}

// --- SoundCloud Widget API loader (once) ---------------------------------
declare global {
  interface Window {
    SC?: { Widget: SCWidgetFactory };
  }
}
type SCWidget = {
  bind: (event: string, cb: (e?: { currentPosition: number }) => void) => void;
  load: (url: string, opts: Record<string, unknown>) => void;
  play: () => void;
  pause: () => void;
  seekTo: (ms: number) => void;
  getDuration: (cb: (ms: number) => void) => void;
};
type SCWidgetFactory = ((el: HTMLIFrameElement) => SCWidget) & {
  Events: Record<string, string>;
};

let scApiPromise: Promise<Window['SC']> | null = null;
function loadScApi(): Promise<Window['SC']> {
  if (window.SC) return Promise.resolve(window.SC);
  if (scApiPromise) return scApiPromise;
  scApiPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://w.soundcloud.com/player/api.js';
    s.async = true;
    s.onload = () => resolve(window.SC);
    s.onerror = () => reject(new Error('Failed to load SoundCloud API'));
    document.body.appendChild(s);
  });
  return scApiPromise;
}

const fmt = (ms: number) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/**
 * A single custom player (play/pause + an interactive seek slider, no artwork),
 * sticky at the bottom of the ballot. A hidden SoundCloud iframe does the audio;
 * we drive it via the Widget API so the controls are our own.
 */
export function PlayerBar({ track, onClose }: { track: { url: string; title: string }; onClose: () => void }) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const widgetRef = useRef<SCWidget | null>(null);
  const seekingRef = useRef(false);
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [pos, setPos] = useState(0);
  const [dur, setDur] = useState(0);

  const initialSrc = useRef(
    'https://w.soundcloud.com/player/?url=' +
      encodeURIComponent(track.url) +
      '&visual=false&auto_play=false',
  );

  // Set up the widget once the iframe + API are available.
  useEffect(() => {
    let cancelled = false;
    loadScApi()
      .then((SC) => {
        if (cancelled || !SC || !iframeRef.current) return;
        const widget = SC.Widget(iframeRef.current);
        widgetRef.current = widget;
        const E = SC.Widget.Events;
        widget.bind(E.READY, () => {
          setReady(true);
          widget.getDuration((d) => setDur(d));
          widget.play();
        });
        widget.bind(E.PLAY, () => setPlaying(true));
        widget.bind(E.PAUSE, () => setPlaying(false));
        widget.bind(E.FINISH, () => setPlaying(false));
        widget.bind(E.PLAY_PROGRESS, (e) => {
          if (!seekingRef.current && e) setPos(e.currentPosition);
        });
      })
      .catch(() => setReady(false));
    return () => {
      cancelled = true;
    };
  }, []);

  // Load a new track when the row changes (without recreating the widget).
  useEffect(() => {
    const widget = widgetRef.current;
    if (!widget || !ready) return;
    widget.load(track.url, {
      auto_play: true,
      visual: false,
      show_artwork: false,
      callback: () => {
        setPos(0);
        widget.getDuration((d) => setDur(d));
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [track.url]);

  const toggle = () => {
    const w = widgetRef.current;
    if (!w) return;
    if (playing) w.pause();
    else w.play();
  };
  const seek = (ms: number) => {
    widgetRef.current?.seekTo(ms);
    setPos(ms);
  };

  return (
    <div className="flex items-center gap-3 py-1">
      <button
        type="button"
        onClick={toggle}
        disabled={!ready}
        title={playing ? 'Pause' : 'Play'}
        className="shrink-0 rounded-full bg-primary p-2 text-primary-foreground disabled:opacity-50"
      >
        {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
      </button>
      <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{fmt(pos)}</span>
      <input
        type="range"
        min={0}
        max={dur || 0}
        value={pos}
        disabled={!ready || dur === 0}
        onMouseDown={() => (seekingRef.current = true)}
        onTouchStart={() => (seekingRef.current = true)}
        onChange={(e) => setPos(Number(e.target.value))}
        onMouseUp={(e) => {
          seek(Number((e.target as HTMLInputElement).value));
          seekingRef.current = false;
        }}
        onTouchEnd={() => {
          seek(pos);
          seekingRef.current = false;
        }}
        className="h-1 flex-1 cursor-pointer"
      />
      <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{fmt(dur)}</span>
      <iframe
        ref={iframeRef}
        src={initialSrc.current}
        title="audio"
        allow="autoplay"
        className="pointer-events-none absolute h-px w-px opacity-0"
      />
      <button
        type="button"
        onClick={onClose}
        title="Close player"
        className="shrink-0 text-muted-foreground hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
